import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useSession } from '@/features/auth/useSession';
import { useCurrentUser } from '@/features/auth/useCurrentUser';
import { signOut } from '@/lib/supabase/auth';
import { FullScreenSpinner } from '@/app/layouts/FullScreenSpinner';
import { AccessDeniedScreen } from '@/routes/auth/AccessDeniedScreen';

/**
 * Two gates, in order: a Supabase session, then an active employee record. They fail
 * differently and must not be collapsed — a signed-in stranger and a signed-in
 * colleague awaiting provisioning need completely different screens.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading } = useSession();
  const location = useLocation();
  const me = useCurrentUser();

  /**
   * A 401 means the stored session cannot be verified — expired, or signed by a key
   * this project does not have, which is what happens when the backend is repointed at
   * a different Supabase project. Holding on to it produces an error screen whose only
   * button (Reload) sends the same dead token again.
   *
   * A session that cannot be verified is not a session. Discard it and start over.
   */
  const unverifiable = me.isError && me.error.status === 401;
  useEffect(() => {
    if (unverifiable) void signOut();
  }, [unverifiable]);

  if (loading) return <FullScreenSpinner label="Checking your session" />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  if (unverifiable) return <FullScreenSpinner label="Signing you out" />;

  if (me.isPending) return <FullScreenSpinner label="Loading your workspace" />;
  if (me.isError) return <AccessDeniedScreen error={me.error} />;

  return <>{children}</>;
}
